<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, inject, proxyRefs, ref } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['edit']);
const realData = inject('imudeiRealData');
const { error, loading, refresh, saving } = realData;
const auditPanel = ref(null);
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'commissions'),
    screen: computed(() => 'detail'),
  })
);

const asArray = value => (Array.isArray(value) ? value : []);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const readable = value => {
  const text = String(value || '').replaceAll('_', ' ');
  return text ? `${text[0].toLocaleUpperCase('pt-BR')}${text.slice(1)}` : '—';
};
const moneyValue = value => {
  if (!value || typeof value !== 'object') return value;
  if (value.formatted || value.display || value.label) {
    return value.formatted || value.display || value.label;
  }
  if (value.amount !== undefined) return value.amount;
  if (value.value !== undefined) return value.value;
  if (value.amount_cents !== undefined) return Number(value.amount_cents) / 100;
  if (value.cents !== undefined) return Number(value.cents) / 100;

  return value.price_amount;
};
const money = (value, currency = 'BRL') => {
  if (value === undefined || value === null || value === '') return '—';

  const normalized = moneyValue(value);
  if (typeof normalized === 'string' && Number.isNaN(Number(normalized))) {
    return normalized;
  }

  const number = Number(normalized);
  if (!Number.isFinite(number)) return String(normalized);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(number);
};
const numberValue = value => {
  const normalized = moneyValue(value);
  if (normalized === undefined || normalized === null || normalized === '') {
    return null;
  }

  const number = Number(String(normalized).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};
const percentageValue = value => {
  if (value === undefined || value === null || value === '') return null;

  const number = Number(String(value).replace('%', '').replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};
const percentage = value => {
  const number = percentageValue(value);
  return number === null ? '—' : `${number}%`;
};
const date = value => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};
const shortDate = value => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(
    parsed
  );
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const mediaUrl = media => {
  if (typeof media === 'string') return media;
  if (Array.isArray(media)) return media.map(mediaUrl).find(Boolean) || '';
  if (!media || typeof media !== 'object') return '';

  return mediaUrl(
    firstValue(media, [
      'url',
      'src',
      'href',
      'image_url',
      'imageUrl',
      'media_url',
      'mediaUrl',
      'preview_url',
    ])
  );
};

const source = computed(() => model.detail?.source || {});
const metadata = computed(() => source.value.metadata || {});
const currency = computed(() => source.value.currency || 'BRL');
const hasAgreement = computed(() => Boolean(model.detail?.id));
const commissionCode = computed(
  () =>
    firstValue(source.value, ['reference', 'code'], '') ||
    (model.detail?.id ? `COM-${model.detail.id}` : 'Comissão')
);
const propertyTitle = computed(
  () =>
    firstValue(source.value, [
      'imovel_listing.title',
      'imovel_listing.titulo',
      'listing.title',
      'listing.titulo',
      'property.title',
      'property.titulo',
      'imovel.title',
      'imovel.titulo',
      'metadata.property_title',
      'metadata.listing_title',
    ]) || 'Imóvel não retornado'
);
const propertyAddress = computed(
  () =>
    firstValue(source.value, [
      'imovel_listing.address',
      'imovel_listing.endereco',
      'listing.address',
      'listing.endereco',
      'property.address',
      'property.endereco',
      'imovel.address',
      'imovel.endereco',
      'metadata.property_address',
      'metadata.listing_address',
    ]) || 'Endereço não retornado'
);
const propertyImage = computed(
  () =>
    firstValue(source.value, [
      'imovel_listing.cover_url',
      'imovel_listing.image_url',
      'listing.cover_url',
      'listing.image_url',
      'property.cover_url',
      'property.image_url',
      'imovel.cover_url',
      'imovel.image_url',
      'metadata.property_image_url',
    ]) ||
    mediaUrl(
      firstValue(source.value, [
        'imovel_listing.midias',
        'listing.midias',
        'property.midias',
        'imovel.midias',
      ])
    )
);
const propertyReference = computed(
  () =>
    firstValue(source.value, [
      'sale_reference',
      'transaction.reference',
      'imovel_listing.reference',
      'listing.reference',
      'metadata.sale_reference',
    ]) || `Listing #${source.value.imovel_listing_id || '—'}`
);
const saleAmount = computed(() =>
  firstValue(source.value, [
    'base_amount',
    'settled_base_amount',
    'versions.0.base_amount',
    'imovel_listing.price_amount',
    'listing.price_amount',
    'imovel.valor_venda',
    'metadata.sale_amount',
    'metadata.property_value',
  ])
);
const commissionGross = computed(() =>
  firstValue(source.value, [
    'commission_amount',
    'versions.0.computed_amount',
    'metadata.commission_gross_amount',
  ])
);
const commissionNet = computed(() =>
  firstValue(source.value, [
    'settled_amount',
    'versions.0.settled_amount',
    'net_amount',
    'metadata.commission_net_amount',
  ])
);
const deductionAmount = computed(() => {
  const gross = numberValue(commissionGross.value);
  const net = numberValue(commissionNet.value);
  if (gross === null || net === null) return null;

  return gross - net;
});
const status = computed(() => {
  const value = String(source.value.status || model.detail?.status || '');
  const labels = {
    active: 'Ativa',
    cancelled: 'Cancelada',
    draft: 'Rascunho',
    settled: 'Liquidada',
    superseded: 'Substituída',
  };

  return labels[value.toLowerCase()] || readable(value);
});
const statusTone = computed(() => {
  const value = String(source.value.status || '').toLowerCase();
  if (value === 'active' || value === 'settled') {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (value === 'draft') return 'bg-violet-50 text-[#5a24c7]';
  if (value === 'superseded' || value === 'cancelled') {
    return 'bg-slate-100 text-slate-600';
  }

  return 'bg-[#f0edf6] text-[#7d87a4]';
});

const rawParticipants = computed(() => {
  const values = [
    source.value.participants,
    metadata.value.participants,
    metadata.value.distribution,
    metadata.value.rateio,
  ];

  return values.find(Array.isArray) || [];
});
const participantRows = computed(() => {
  if (rawParticipants.value.length) {
    return rawParticipants.value.map((participant, index) => {
      const participantId = firstValue(participant, [
        'id',
        'user_id',
        'beneficiary_user_id',
        'team_id',
        'beneficiary_team_id',
      ]);
      const share = firstValue(participant, [
        'percentage',
        'share_percentage',
        'current_percentage',
        'new_percentage',
        'after_percentage',
        'comparison.current',
        'comparison.new',
      ]);

      return {
        gross: firstValue(participant, [
          'gross_amount',
          'amount',
          'commission_amount',
        ]),
        id: participantId || `${index}`,
        name:
          firstValue(participant, ['name', 'beneficiary.name', 'user.name']) ||
          (participantId ? `Beneficiário #${participantId}` : 'Não informado'),
        net: firstValue(participant, [
          'net_amount',
          'settled_amount',
          'amount',
        ]),
        role: readable(
          firstValue(participant, ['role', 'kind', 'beneficiary_kind'])
        ),
        share,
        status: readable(
          firstValue(participant, ['status', 'state'], status.value)
        ),
      };
    });
  }

  if (!hasAgreement.value) return [];

  const beneficiaryKind = source.value.beneficiary_kind;
  const beneficiaryId =
    source.value.beneficiary_user_id || source.value.beneficiary_team_id;
  let name = 'Beneficiário não retornado';
  if (beneficiaryKind === 'imobiliaria') name = 'Imobiliária';
  if (source.value.beneficiary_user_id) {
    name = `Usuário #${source.value.beneficiary_user_id}`;
  }
  if (source.value.beneficiary_team_id) {
    name = `Equipe #${source.value.beneficiary_team_id}`;
  }

  return [
    {
      gross: commissionGross.value,
      id: beneficiaryId || source.value.id,
      name,
      net: commissionNet.value,
      role: readable(beneficiaryKind),
      share: null,
      status: status.value,
    },
  ];
});
const rateioTotal = computed(() => {
  if (!participantRows.value.length) return '—';

  const values = participantRows.value.map(row => percentageValue(row.share));
  if (values.some(value => value === null)) return '—';

  const total = values.reduce((sum, value) => sum + value, 0);
  return `${Number(total.toFixed(4))}%`;
});
const rateioValid = computed(() => rateioTotal.value === '100%');
const totalGross = computed(() => {
  const amounts = participantRows.value.map(row => numberValue(row.gross));
  if (amounts.length && amounts.every(value => value !== null)) {
    return amounts.reduce((sum, value) => sum + value, 0);
  }

  return commissionGross.value;
});
const totalNet = computed(() => {
  const amounts = participantRows.value.map(row => numberValue(row.net));
  if (amounts.length && amounts.every(value => value !== null)) {
    return amounts.reduce((sum, value) => sum + value, 0);
  }

  return commissionNet.value;
});

const directInstallments = computed(() => {
  const values = [
    source.value.installments,
    source.value.parcelas,
    metadata.value.installments,
    metadata.value.parcelas,
  ];

  return values.find(Array.isArray) || [];
});
const matchingLedger = computed(() => {
  const agreementId = String(model.detail?.id || '');
  if (!agreementId) return [];

  return asArray(realData.data.value?.ledger).filter(row =>
    [
      firstValue(row, ['agreement_id']),
      firstValue(row, ['commission_agreement_id']),
      firstValue(row, ['source.agreement_id']),
      firstValue(row, ['source.commission_agreement_id']),
    ]
      .filter(Boolean)
      .map(value => String(value))
      .includes(agreementId)
  );
});
const installments = computed(() => {
  const records = directInstallments.value.length
    ? directInstallments.value
    : matchingLedger.value;

  return records.map((installment, index) => ({
    expectedAt: firstValue(installment, [
      'expected_on',
      'expected_at',
      'due_at',
      'scheduled_for',
      'date',
    ]),
    id:
      firstValue(installment, ['id', 'parcela_id', 'installment_id']) ||
      `${index}`,
    paid: firstValue(installment, [
      'paid_amount',
      'settled_amount',
      'paid_value',
      'valor_pago',
    ]),
    planned: firstValue(installment, [
      'amount',
      'value',
      'valor',
      'amount_cents',
      'planned_amount',
    ]),
    sequence: firstValue(installment, [
      'sequence',
      'number',
      'installment_number',
      'numero',
    ]),
    status: readable(
      firstValue(installment, ['status', 'state'], 'Não informado')
    ),
  }));
});
const paymentDetails = computed(
  () =>
    firstValue(source.value, [
      'payment_details',
      'payment',
      'metadata.payment_details',
      'metadata.payment',
    ]) || {}
);
const paymentFacts = computed(() =>
  [
    ['Titular', ['holder_name', 'titular', 'account_holder']],
    ['Banco', ['bank_name', 'banco']],
    ['Agência', ['agency', 'agencia']],
    ['Conta', ['account_number', 'conta']],
    ['PIX', ['pix_key', 'pix']],
  ]
    .map(([label, paths]) => ({
      label,
      value: firstValue(paymentDetails.value, paths),
    }))
    .filter(item => item.value)
);
const rawVersions = computed(() => {
  const values = [source.value.versions, source.value.audit, model.history];
  return values.find(value => asArray(value).length) || [];
});
const audit = computed(() =>
  rawVersions.value.map((event, index) => {
    const action = String(
      firstValue(event, ['change_kind', 'label', 'action', 'event_type'])
    ).toLowerCase();
    const labels = {
      activated: 'Acordo publicado',
      created: 'Comissão criada',
      published: 'Acordo publicado',
      settled: 'Comissão liquidada',
      updated: 'Alterações registradas',
    };
    const actorId = firstValue(event, ['actor_id']);

    return {
      actor:
        firstValue(event, ['actor.name', 'user.name']) ||
        (actorId ? `Usuário #${actorId}` : '') ||
        firstValue(event, ['actor_type'], 'Sistema'),
      date: date(
        firstValue(event, [
          'recorded_at',
          'occurred_at',
          'created_at',
          'updated_at',
        ])
      ),
      id: firstValue(event, ['id', 'version_number'], `${action}-${index}`),
      label: labels[action] || readable(action),
      status: readable(firstValue(event, ['status', 'state'], status.value)),
    };
  })
);
const lifecycle = computed(() => {
  const actionKinds = rawVersions.value.map(event =>
    String(
      firstValue(event, ['change_kind', 'action', 'event_type'])
    ).toLowerCase()
  );
  const agreementStatus = String(source.value.status || '').toLowerCase();
  const ledgerStatuses = matchingLedger.value.map(row =>
    String(firstValue(row, ['status', 'state'])).toLowerCase()
  );

  return [
    {
      complete: Boolean(model.detail?.id),
      label: 'Criada',
      value: date(firstValue(source.value, ['created_at'])),
    },
    {
      complete:
        actionKinds.includes('activated') ||
        actionKinds.includes('published') ||
        ['active', 'settled'].includes(agreementStatus),
      label: 'Publicada',
      value: actionKinds.includes('activated') ? 'Registrada' : 'Sem registro',
    },
    {
      complete:
        actionKinds.includes('settled') || agreementStatus === 'settled',
      label: 'Liquidada',
      value: source.value.settled_at
        ? shortDate(source.value.settled_at)
        : 'Sem registro',
    },
    {
      complete: ledgerStatuses.some(value => /payable|a_pagar/.test(value)),
      label: 'Pagável',
      value: matchingLedger.value.length ? 'Ledger vinculado' : 'Sem registro',
    },
    {
      complete: ledgerStatuses.some(value => /paid|pago/.test(value)),
      label: 'Paga',
      value: matchingLedger.value.length ? 'Ledger vinculado' : 'Sem registro',
    },
  ];
});
const documents = computed(() => {
  const values = [
    source.value.documents,
    source.value.documentos,
    metadata.value.documents,
    metadata.value.documentos,
  ];

  return values.find(Array.isArray) || [];
});
const tabs = computed(() => [
  { label: 'Resumo' },
  { label: 'Rateio', count: participantRows.value.length || null },
  { label: 'Participantes', count: participantRows.value.length || null },
  { label: 'Parcelas', count: installments.value.length || null },
  { label: 'Descontos', count: deductionAmount.value === null ? null : 1 },
  { label: 'Documentos', count: documents.value.length || null },
  { label: 'Histórico', count: audit.value.length || null },
]);
const availableActions = computed(() => {
  const actions = model.actionOptions || [];
  return [...new Map(actions.map(action => [action.name, action])).values()];
});
const actionIcon = name => {
  if (name === 'publishAgreement') return 'i-lucide-send';
  if (name === 'markPaid') return 'i-lucide-badge-check';
  if (name === 'markPayable') return 'i-lucide-circle-check-big';

  return 'i-lucide-play';
};
const actionClass = name =>
  name === 'publishAgreement'
    ? 'bg-[#5120bb] text-white'
    : 'border border-violet-200 text-[#5522bf]';

const performAction = async name => {
  await model.runAction(name);
};
const scrollToAudit = () =>
  auditPanel.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    data-imudei-visual-id="commission-detail-root"
    class="ml-12 mr-5 space-y-4 text-[#101958]"
  >
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p class="text-[10px] text-[#725bd0]">
          Comissões <span class="mx-2 text-[#a5adc1]">›</span
          >{{ commissionCode }}
        </p>
        <div class="mt-1 flex flex-wrap items-center gap-3">
          <h1 class="text-[25px] font-bold">
            Comissão — {{ propertyReference }}
          </h1>
          <span class="rounded-full px-3 py-1 text-[10px]" :class="statusTone">
            {{ status }}
          </span>
          <span
            class="rounded-full bg-violet-50 px-3 py-1 text-[10px] text-[#5522bf]"
          >
            <i
              class="i-lucide-shield-check mr-1 inline-block size-3"
            />Auditável
          </span>
        </div>
      </div>
      <div class="flex flex-wrap items-center justify-end gap-2">
        <button
          class="rounded-lg border border-violet-200 px-4 py-2 text-[10px] font-bold text-[#5522bf] disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!model.canEdit"
          type="button"
          @click="emit('edit')"
        >
          <i class="i-lucide-pencil mr-2 inline-block size-3.5" />Editar acordo
        </button>
        <button
          v-for="action in availableActions"
          :key="action.name"
          class="rounded-lg px-4 py-2 text-[10px] font-bold disabled:cursor-not-allowed disabled:opacity-50"
          :class="actionClass(action.name)"
          :disabled="saving"
          type="button"
          @click="performAction(action.name)"
        >
          <i
            :class="actionIcon(action.name)"
            class="mr-2 inline-block size-3.5"
          />
          {{ saving ? 'Processando…' : action.label }}
        </button>
        <button
          v-if="!availableActions.length"
          class="rounded-lg border border-dashed border-[#d8ddeb] px-4 py-2 text-[10px] font-bold text-[#7a85a6]"
          disabled
          type="button"
        >
          Nenhuma ação permitida
        </button>
        <button
          class="grid size-9 place-items-center rounded-lg border border-[#e8e5f1] disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!audit.length"
          type="button"
          @click="scrollToAudit"
        >
          <i class="i-lucide-more-horizontal size-4" />
        </button>
      </div>
    </header>

    <section
      v-if="!hasAgreement"
      class="grid min-h-[340px] place-items-center rounded-2xl border border-dashed border-[#d8ddeb] bg-white p-6 text-center"
    >
      <div>
        <i
          :class="
            loading
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-lucide-file-search'
          "
          class="mx-auto size-7 text-[#7a85a6]"
        />
        <strong class="mt-3 block text-[12px]"
          >Detalhe da comissão indisponível</strong
        >
        <small class="mt-1 block text-[9px] text-[#6d79a0]">
          {{
            loading
              ? 'Carregando o acordo selecionado.'
              : 'A API não retornou um acordo para esta rota.'
          }}
        </small>
        <button
          class="mt-4 rounded-lg border border-violet-200 px-4 py-2 text-[9px] font-bold text-[#5522bf]"
          type="button"
          @click="refresh"
        >
          Atualizar dados
        </button>
      </div>
    </section>

    <template v-else>
      <section
        class="grid grid-cols-[1.2fr_1fr_1fr_1fr_1.1fr] gap-3 rounded-2xl border border-[#efedf6] bg-white p-4"
      >
        <div class="flex min-w-0 gap-3">
          <img
            v-if="propertyImage"
            :src="propertyImage"
            :alt="propertyTitle"
            class="size-20 rounded-xl object-cover"
          />
          <span
            v-else
            class="grid size-20 shrink-0 place-items-center rounded-xl bg-[#f0edf6] text-[#7a85a6]"
          >
            <i class="i-lucide-building-2 size-7" />
          </span>
          <span class="min-w-0">
            <strong class="block truncate text-[13px] text-[#4e21b7]">{{
              propertyReference
            }}</strong>
            <small class="mt-1 block truncate text-[10px]">{{
              propertyTitle
            }}</small>
            <small class="mt-2 block truncate text-[#68759d]">{{
              propertyAddress
            }}</small>
            <small class="mt-1 block text-[#68759d]">{{
              date(source.created_at)
            }}</small>
          </span>
        </div>
        <div>
          <small class="text-[9px] text-[#68759d]">Base de cálculo</small>
          <strong class="mt-3 block text-[18px]">{{
            money(saleAmount, currency)
          }}</strong>
        </div>
        <div>
          <small class="text-[9px] text-[#68759d]">Comissão bruta</small>
          <strong class="mt-3 block text-[18px] text-emerald-600">{{
            money(commissionGross, currency)
          }}</strong>
        </div>
        <div>
          <small class="text-[9px] text-[#68759d]">Descontos</small>
          <strong class="mt-3 block text-[18px] text-rose-500">{{
            money(deductionAmount, currency)
          }}</strong>
        </div>
        <div class="rounded-xl bg-violet-50 p-3">
          <small class="text-[9px] text-[#5a26c5]">Comissão líquida</small>
          <strong class="mt-2 block text-[28px] text-[#4820b5]">{{
            money(commissionNet, currency)
          }}</strong>
        </div>
      </section>

      <nav class="flex gap-6 overflow-x-auto border-b border-[#e9e7f2] px-4">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.label"
          class="whitespace-nowrap border-b-2 pb-3 text-[10px]"
          :class="
            index === 0
              ? 'border-[#5522bf] font-bold text-[#5522bf]'
              : 'border-transparent text-[#65729c]'
          "
          type="button"
        >
          {{ tab.label }}
          <span
            v-if="tab.count !== null"
            class="ml-1 rounded-full bg-[#f0edf6] px-1.5 py-0.5 text-[8px] text-[#5522bf]"
            >{{ tab.count }}</span
          >
        </button>
      </nav>

      <div class="grid grid-cols-[minmax(0,1fr)_435px] items-start gap-4">
        <section
          class="min-h-[359px] rounded-2xl border border-[#efedf6] bg-white p-4"
        >
          <header class="flex items-center justify-between gap-3">
            <h2 class="text-[13px] font-bold">
              Rateio da comissão
              <span
                class="ml-2 rounded-full px-2 py-1 text-[9px]"
                :class="
                  rateioValid
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-violet-50 text-[#5522bf]'
                "
                >{{
                  rateioValid
                    ? '100% do líquido'
                    : rateioTotal === '—'
                      ? 'Não retornado'
                      : `${rateioTotal} do líquido`
                }}</span
              >
            </h2>
            <span class="text-[9px] text-[#6d79a0]">Trilha auditável</span>
          </header>

          <div class="mt-5 flex items-start">
            <span
              v-for="(step, index) in lifecycle"
              :key="step.label"
              class="relative flex flex-1 flex-col items-center text-center"
            >
              <i
                class="z-10 grid size-7 place-items-center rounded-full text-white"
                :class="
                  step.complete ? 'bg-[#5522bf]' : 'bg-[#d8ddeb] text-[#7a85a6]'
                "
              >
                <i
                  :class="step.complete ? 'i-lucide-check' : 'i-lucide-minus'"
                  class="size-3.5"
                />
              </i>
              <small class="mt-2 text-[9px]">{{ step.label }}</small>
              <small class="mt-1 text-[7px] text-[#6d79a0]">{{
                step.value
              }}</small>
              <i
                v-if="index < lifecycle.length - 1"
                class="absolute left-[50%] top-3 h-0.5 w-full bg-[#ded9e8]"
              />
            </span>
          </div>

          <div class="mt-5 divide-y divide-[#efedf6]">
            <div
              class="grid grid-cols-[1.4fr_1fr_110px_130px_130px_70px] gap-3 pb-2 text-[9px] font-bold text-[#6d79a0]"
            >
              <span>Participante</span><span>Função</span
              ><span>% do líquido</span><span>Bruto</span><span>Líquido</span
              ><span>Status</span>
            </div>
            <div
              v-for="row in participantRows"
              :key="row.id"
              class="grid grid-cols-[1.4fr_1fr_110px_130px_130px_70px] items-center gap-3 py-3 text-[10px]"
            >
              <span class="flex min-w-0 items-center gap-2">
                <i
                  class="grid size-9 shrink-0 place-items-center rounded-full bg-violet-100 font-bold text-[#5522bf]"
                  >{{ initials(row.name) }}</i
                >
                <span class="min-w-0"
                  ><strong class="block truncate">{{ row.name }}</strong
                  ><small class="block truncate text-[8px] text-[#6d79a0]"
                    >Beneficiário retornado pela API</small
                  ></span
                >
              </span>
              <span>{{ row.role }}</span>
              <span>{{ percentage(row.share) }}</span>
              <span>{{ money(row.gross, currency) }}</span>
              <span>{{ money(row.net, currency) }}</span>
              <span
                class="rounded bg-violet-50 px-2 py-1 text-center text-[8px] font-bold text-[#5522bf]"
                >{{ row.status }}</span
              >
            </div>
            <div
              v-if="!participantRows.length"
              class="grid min-h-20 place-items-center text-[9px] text-[#6d79a0]"
            >
              Nenhum participante retornado para este acordo.
            </div>
            <div
              class="grid grid-cols-[1.4fr_1fr_110px_130px_130px_70px] gap-3 border-t border-[#efedf6] pt-3 text-[10px] font-bold"
            >
              <span>Total</span><span /><span>{{ rateioTotal }}</span
              ><span>{{ money(totalGross, currency) }}</span
              ><span>{{ money(totalNet, currency) }}</span
              ><span />
            </div>
          </div>
          <p class="mt-3 flex items-center gap-2 text-[8px] text-[#6d79a0]">
            <i class="i-lucide-shield-check size-3.5 text-[#5522bf]" />
            Valores líquidos só são exibidos quando retornados pelo acordo.
          </p>
        </section>

        <aside
          ref="auditPanel"
          class="min-h-[419px] rounded-2xl border border-[#efedf6] bg-white p-4"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[13px] font-bold">Atividade</h2>
            <span class="text-[9px] text-[#5522bf]">{{
              audit.length ? 'Registros' : 'Sem registros'
            }}</span>
          </header>
          <ol v-if="audit.length" class="mt-4 space-y-4">
            <li
              v-for="(event, index) in audit.slice(0, 5)"
              :key="event.id"
              class="relative flex gap-3"
            >
              <i
                v-if="index < audit.slice(0, 5).length - 1"
                class="absolute left-3.5 top-7 h-[calc(100%+9px)] w-px bg-[#e4def0]"
              />
              <span
                class="z-10 grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5522bf]"
                ><i class="i-lucide-shield-check size-3.5"
              /></span>
              <span class="min-w-0 flex-1"
                ><strong class="block text-[10px]">{{ event.label }}</strong
                ><small class="mt-1 block truncate text-[8px] text-[#6d79a0]"
                  >{{ event.actor }} · {{ event.date }}</small
                ><small class="mt-1 block text-[8px] text-[#6d79a0]">{{
                  event.status
                }}</small></span
              >
            </li>
            <li
              v-for="slot in Math.max(0, 5 - audit.length)"
              :key="`audit-unavailable-${slot}`"
              class="relative flex gap-3"
            >
              <i
                class="grid size-7 shrink-0 place-items-center rounded-full border border-dashed border-[#d8ddeb] bg-[#faf9fd] text-[#7a85a6]"
              >
                <i class="i-lucide-circle-slash-2 size-3.5" />
              </i>
              <span class="min-w-0 flex-1">
                <strong class="block text-[10px]"
                  >Atividade indisponível</strong
                >
                <small class="mt-1 block text-[8px] text-[#6d79a0]"
                  >A API não retornou outro registro auditável.</small
                >
              </span>
            </li>
          </ol>
          <div
            v-else
            class="mt-4 grid min-h-[220px] place-items-center rounded-xl border border-dashed border-[#d8ddeb] bg-[#faf9fd] p-4 text-center"
          >
            <span
              ><i
                class="i-lucide-history mx-auto size-5 text-[#7a85a6]"
              /><strong class="mt-2 block text-[9px]"
                >Histórico não retornado</strong
              ><small class="mt-1 block text-[8px] text-[#6d79a0]"
                >O endpoint não forneceu versões para este acordo.</small
              ></span
            >
          </div>
        </aside>
      </div>

      <div class="!-mt-[44px] grid grid-cols-[1fr_1fr_435px] gap-4">
        <section
          class="h-[228px] rounded-2xl border border-[#efedf6] bg-white p-4"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[12px] font-bold">Parcelas da comissão</h2>
            <span class="text-[8px] text-[#6d79a0]">{{
              installments.length
                ? `${installments.length} retornada(s)`
                : 'Sem endpoint no acordo'
            }}</span>
          </header>
          <div v-if="installments.length">
            <div
              v-for="(installment, index) in installments.slice(0, 3)"
              :key="installment.id"
              class="mt-4 flex items-center gap-3"
            >
              <span
                class="grid size-6 place-items-center rounded-full"
                :class="
                  /pago|paid|liquid/.test(installment.status.toLowerCase())
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'border border-[#d8ddeb] text-[#7a85a6]'
                "
                ><i
                  :class="
                    /pago|paid|liquid/.test(installment.status.toLowerCase())
                      ? 'i-lucide-check'
                      : 'i-lucide-minus'
                  "
                  class="size-3"
              /></span>
              <span class="min-w-0 flex-1"
                ><strong class="block text-[10px]">{{
                  installment.sequence
                    ? `${installment.sequence}ª parcela`
                    : `Parcela ${index + 1}`
                }}</strong
                ><small class="text-[8px] text-[#6d79a0]">{{
                  shortDate(installment.expectedAt)
                }}</small></span
              >
              <span class="text-right text-[8px]"
                ><small class="block text-[#6d79a0]">Previsto</small
                ><b>{{ money(installment.planned, currency) }}</b></span
              >
              <span
                class="rounded bg-violet-50 px-2 py-1 text-[8px] font-bold text-[#5522bf]"
                >{{ installment.status }}</span
              >
            </div>
          </div>
          <div
            v-else
            class="mt-4 grid min-h-[120px] place-items-center rounded-xl border border-dashed border-[#d8ddeb] bg-[#faf9fd] p-3 text-center"
          >
            <span
              ><i
                class="i-lucide-calendar-x2 mx-auto size-5 text-[#7a85a6]"
              /><strong class="mt-2 block text-[9px]"
                >Parcelas não retornadas</strong
              ><small class="mt-1 block text-[8px] text-[#6d79a0]"
                >Este endpoint de acordo não expõe o cronograma
                financeiro.</small
              ></span
            >
          </div>
        </section>

        <section
          class="h-[228px] rounded-2xl border border-[#efedf6] bg-white p-4"
        >
          <h2 class="text-[12px] font-bold">Dados para pagamento</h2>
          <dl v-if="paymentFacts.length" class="mt-4 space-y-3 text-[10px]">
            <div
              v-for="fact in paymentFacts"
              :key="fact.label"
              class="flex justify-between gap-3"
            >
              <dt class="text-[#6d79a0]">{{ fact.label }}</dt>
              <dd class="truncate text-right font-semibold">
                {{ fact.value }}
              </dd>
            </div>
          </dl>
          <div
            v-else
            class="mt-4 grid min-h-[120px] place-items-center rounded-xl border border-dashed border-[#d8ddeb] bg-[#faf9fd] p-3 text-center"
          >
            <span
              ><i
                class="i-lucide-lock-keyhole mx-auto size-5 text-[#7a85a6]"
              /><strong class="mt-2 block text-[9px]"
                >Dados bancários indisponíveis</strong
              ><small class="mt-1 block text-[8px] text-[#6d79a0]"
                >O acordo não expõe dados sensíveis de pagamento.</small
              ></span
            >
          </div>
          <p class="mt-5 text-[8px] text-[#6d79a0]">
            <i class="i-lucide-lock mr-1 inline-block size-3" />Dados bancários
            são exibidos somente quando a API os autoriza.
          </p>
        </section>

        <aside
          class="h-[228px] overflow-hidden rounded-2xl border border-[#efedf6] bg-white p-4"
        >
          <header class="flex items-center justify-between gap-3">
            <span class="flex items-center gap-2 text-[10px] font-bold">
              <i
                class="i-lucide-medal size-4 rounded-full bg-amber-50 p-0.5 text-amber-500"
              />
              Sua missão diária
            </span>
            <span class="text-[8px] text-[#6d79a0]">Sem dados</span>
          </header>
          <div class="mt-4 rounded-xl bg-[#faf9fd] px-3 py-3">
            <strong class="block text-[11px]">Gamificação indisponível</strong>
            <p class="mt-1 text-[8px] leading-4 text-[#6d79a0]">
              Esta rota não retorna pontos, progresso ou conquistas vinculadas à
              comissão.
            </p>
            <div class="mt-3 h-1.5 rounded-full bg-[#e9e7f2]">
              <i class="block h-full w-0 rounded-full bg-[#5522bf]" />
            </div>
          </div>
          <div class="mt-3 grid grid-cols-3 gap-2 text-[8px]">
            <span class="rounded-lg border border-[#efedf6] px-2 py-1.5">
              <small class="block text-[#6d79a0]">Pontos</small>
              <strong>—</strong>
            </span>
            <span class="rounded-lg border border-[#efedf6] px-2 py-1.5">
              <small class="block text-[#6d79a0]">Progresso</small>
              <strong>Não retornado</strong>
            </span>
            <span class="rounded-lg border border-[#efedf6] px-2 py-1.5">
              <small class="block text-[#6d79a0]">Prêmios</small>
              <strong>Indisponíveis</strong>
            </span>
          </div>
        </aside>
      </div>

      <footer
        class="flex items-center justify-between gap-4 rounded-2xl border border-[#efedf6] bg-white px-4 py-3 text-[9px] text-[#6d79a0]"
      >
        <span
          ><i class="i-lucide-info mr-1 inline-block size-3" />{{
            model.records.length
          }}
          registro(s) de comissão carregado(s)</span
        >
        <span
          >Documentos e pontos só aparecem quando forem retornados pela
          API.</span
        >
      </footer>
    </template>

    <p
      v-if="model.notice"
      class="fixed bottom-7 right-7 z-50 max-w-sm rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[9px] leading-4 text-emerald-700"
    >
      {{ model.notice }}
    </p>
    <p
      v-if="model.localError || error"
      class="fixed bottom-7 right-7 z-50 max-w-sm rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[9px] leading-4 text-rose-600"
    >
      {{ model.localError || error }}
    </p>
  </section>
</template>
